/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// ForgotPassword.tsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const { requestPasswordReset, loading, error, clearError } = useAuth();
  const { isDark } = useDarkMode();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    clearError();

    try {
      await requestPasswordReset(email);
      setMessage("If this email is registered, a reset link has been sent.");
    } catch (error: any) {
      setMessage(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-6 py-12 transition-colors duration-300 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 to-gray-800'
          : 'bg-gradient-to-br from-sky-50 to-blue-50'
      }`}
    >
      <Helmet>
        <title>Reset Your Password | FindWork</title>
        <meta
          name="description"
          content="Forgot your FindWork password? Reset it quickly and get back to finding jobs or managing your account."
        />
        <link rel="canonical" href="https://findwork.ltd/forgot-password" />
      </Helmet>
      
      <div
        className={`w-full max-w-md shadow-lg rounded-2xl p-8 border transition-colors duration-300 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-sky-100'
        }`}
      >
        {/* Header */}
        <h1 className="text-2xl font-bold text-center bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
          Forgot Password
        </h1>
        <p
          className={`text-center mt-2 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Enter your email and we'll send you a reset link.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* Email */}
          <div>
            <label
              className={`block font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-sky-200 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="example@email.com"
            />
          </div>

          {/* Message */}
          {message && (
            <div
              className={`p-3 rounded-md border ${
                message.includes('sent')
                  ? isDark
                    ? 'bg-green-900/30 text-green-300 border-green-800'
                    : 'bg-green-50 text-green-700 border-green-200'
                  : isDark
                    ? 'bg-red-900/30 text-red-300 border-red-800'
                    : 'bg-red-50 text-red-700 border-red-200'
              }`}
            >
              {message}
            </div>
          )}

          {/* Error from AuthContext */}
          {error && (
            <div
              className={`p-3 rounded-md border ${
                isDark
                  ? 'bg-red-900/30 text-red-300 border-red-800'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
            >
              {error.toString()}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || loading}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-md hover:from-sky-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading || loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        {/* Footer */}
        <p
          className={`text-center mt-6 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          Remember your password?{' '}
          <Link
            to="/login"
            className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium hover:underline"
          >
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}
